import type { Unit } from '../types';
import { loadModules, loadUnit } from '../data/content-loader';
import { progressStore } from './progress';
import { renderSlide, wireSlideInteractions } from './slide-renderer';

type ProgressRailElement = HTMLElement & { setProgress?: (current: number, total: number) => void };

interface Shell {
  root: HTMLElement;
  unitLabel: HTMLElement;
  prev: HTMLButtonElement;
  next: HTMLButtonElement;
  rail: ProgressRailElement;
}

export class SlideRouter {
  private unit: Unit | null = null;
  private index = 0;

  constructor(private readonly shell: Shell) {
    wireSlideInteractions(shell.root, () => void this.completeCurrent());
  }

  async start(): Promise<void> {
    await this.openFromLocation();
    window.addEventListener('hashchange', () => void this.openFromLocation());
    this.shell.prev.addEventListener('click', () => this.go(-1));
    this.shell.next.addEventListener('click', () => this.go(1));
    window.addEventListener('keydown', (event) => this.onKey(event));
  }

  private async openFromLocation(): Promise<void> {
    const route = parseHash(location.hash);
    if (route.kind === 'modules') {
      this.unit = null;
      this.index = 0;
      this.renderModules(await loadModules());
      return;
    }

    this.unit = await loadUnit(route.moduleId, route.unitIndex);
    this.index = Math.min(Math.max(route.slideIndex - 1, 0), this.unit.slides.length - 1);
    this.render();
  }

  private render(): void {
    if (!this.unit) return;
    const slide = this.unit.slides[this.index];
    if (!slide) return;

    document.documentElement.dataset.view = 'slide';
    document.documentElement.dataset.slideType = slide.type;
    this.shell.root.classList.remove('slide--wide');
    renderSlide(this.shell.root, this.unit, slide);
    this.shell.prev.disabled = this.index === 0;
    this.shell.next.disabled = false;
    this.shell.next.textContent = this.index === this.unit.slides.length - 1 ? 'Finish' : 'Next';
    this.shell.rail.setProgress?.(slide.index, this.unit.slides.length);
    void progressStore.markVisited(this.unit.id, slide.id);
    this.updateHash();
  }

  private renderModules(modules: Awaited<ReturnType<typeof loadModules>>): void {
    document.documentElement.dataset.view = 'dashboard';
    delete document.documentElement.dataset.slideType;
    this.shell.root.classList.remove('slide--landscape');
    this.shell.root.classList.add('slide--wide');
    this.shell.root.innerHTML = `
      <section class="module-dashboard" aria-label="Modules">
        <div class="module-card-grid">
          ${modules.map((module, index) => moduleCard(module, index)).join('')}
        </div>
      </section>
    `;
    this.shell.prev.disabled = true;
    this.shell.next.disabled = true;
    this.shell.next.textContent = 'Choose a module';
    this.shell.rail.setProgress?.(0, modules.length);
  }

  private go(delta: number): void {
    if (!this.unit) return;
    void this.completeCurrent();
    const next = this.index + delta;
    if (next < 0) return;
    if (next >= this.unit.slides.length) {
      this.shell.root.dispatchEvent(new CustomEvent('unit-finished', { bubbles: true, detail: { unitId: this.unit.id } }));
      return;
    }
    this.index = next;
    this.render();
  }

  private async completeCurrent(): Promise<void> {
    const slide = this.unit?.slides[this.index];
    if (!this.unit || !slide) return;
    await progressStore.markCompleted(this.unit.id, slide.id);
  }

  private updateHash(): void {
    if (!this.unit) return;
    const wanted = `#/${this.unit.moduleId}/u${pad2(this.unit.index)}/s${pad2(this.index + 1)}`;
    if (location.hash !== wanted) {
      history.replaceState(null, '', wanted);
      // replaceState doesn't fire hashchange; tell listeners (e.g. sidebar)
      // that the route changed so they can re-highlight.
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  }

  private onKey(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement | null;
    if (target?.matches('input, textarea, select, button')) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(1);
    }
  }
}

type Route =
  | { kind: 'modules' }
  | { kind: 'slide'; moduleId: 'm1' | 'm2' | 'm3'; unitIndex: number; slideIndex: number };

function parseHash(hash: string): Route {
  if (!hash || hash === '#/' || hash === '#/modules') return { kind: 'modules' };
  const match = /^#\/((?:m1|m2|m3))\/u(\d{2})\/s(\d{2})$/.exec(hash);
  if (!match) return { kind: 'modules' };
  return { kind: 'slide', moduleId: match[1] as 'm1' | 'm2' | 'm3', unitIndex: Number(match[2]), slideIndex: Number(match[3]) };
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function moduleCard(module: Awaited<ReturnType<typeof loadModules>>[number], index: number): string {
  const firstUnit = module.units[0];
  const href = firstUnit ? `#/${module.id}/u${pad2(firstUnit.index)}/s01` : '#/modules';
  const outcomes = (module.outcomes ?? []).map((o) => `<li>${escapeHtml(o)}</li>`).join('');

  return `
    <a class="module-card" href="${href}" data-status="${module.status}" data-module="${module.id}">
      <span class="module-card-header">
        <span class="module-card-number">Module ${index + 1}</span>
        <span class="module-card-cefr">${module.totalSlides.toLocaleString()} slides</span>
      </span>
      <span class="module-card-art" aria-hidden="true"></span>
      <span class="module-card-body">
        <span class="module-card-title">${escapeHtml(module.title)}</span>
        <span class="module-card-sub">${escapeHtml(module.subtitle)}</span>
        ${outcomes ? `<ul class="module-card-outcomes">${outcomes}</ul>` : ''}
      </span>
      <span class="module-card-foot">
        <span class="module-card-cta">Start module</span>
      </span>
    </a>
  `;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
}
